import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util';
import type { EnvelopePayload } from '../types';

/**
 * Out-of-band identity verification number, computed from both parties'
 * identity public keys. Signal calls this a "safety number". The pair sorts
 * the two keys so both sides compute the same string; if Alice's app and
 * Bob's app show the same number when they meet in person, no machine-in-
 * the-middle is sitting between them.
 *
 * Returns 30 hex chars grouped 6x5 — e.g. "a3f12 b4c5d e6f78 9012a bcdef 01234".
 */
export function safetyNumber(my_pubkey_b64: string, their_pubkey_b64: string): string {
  const [a, b] = [my_pubkey_b64, their_pubkey_b64].sort();
  const combined = decodeUTF8(a + b);
  const hash = nacl.hash(combined);
  const slice = hash.slice(0, 15);
  const hex = Array.from(slice)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return hex.match(/.{5}/g)!.join(' ');
}

export type RawKeyPair = {
  publicKey_b64: string;
  privateKey_b64: string;
};

export function generateIdentityKeyPair(): RawKeyPair {
  const kp = nacl.box.keyPair();
  return {
    publicKey_b64: encodeBase64(kp.publicKey),
    privateKey_b64: encodeBase64(kp.secretKey),
  };
}

/**
 * Sealed-sender style. We generate an ephemeral keypair on the sender side
 * for every message, do an authenticated box to the recipient, and pack
 * `ephemeral_pubkey || nonce || ciphertext` into the envelope.
 *
 * The recipient can decrypt without knowing the sender's identity from the
 * wire. The relay sees only an opaque blob addressed to a recipient mailbox.
 *
 * (For Phase 0 the sender's username travels inside the encrypted payload
 * so the recipient can display it. Phase 1 with libsignal adds a signed
 * sender certificate so the recipient can cryptographically verify it.)
 */
export function sealForRecipient(
  payload: EnvelopePayload,
  recipient_pubkey_b64: string,
): string {
  const recipient_pk = decodeBase64(recipient_pubkey_b64);
  const ephemeral = nacl.box.keyPair();
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const plaintext = decodeUTF8(JSON.stringify(payload));
  const ciphertext = nacl.box(plaintext, nonce, recipient_pk, ephemeral.secretKey);

  const packed = new Uint8Array(
    ephemeral.publicKey.length + nonce.length + ciphertext.length,
  );
  packed.set(ephemeral.publicKey, 0);
  packed.set(nonce, ephemeral.publicKey.length);
  packed.set(ciphertext, ephemeral.publicKey.length + nonce.length);

  return encodeBase64(packed);
}

export function openSealed(
  ciphertext_b64: string,
  my_seckey_b64: string,
): EnvelopePayload | null {
  try {
    const packed = decodeBase64(ciphertext_b64);
    const pkLen = nacl.box.publicKeyLength;
    const nonceLen = nacl.box.nonceLength;
    if (packed.length < pkLen + nonceLen) return null;

    const ephemeral_pk = packed.slice(0, pkLen);
    const nonce = packed.slice(pkLen, pkLen + nonceLen);
    const ciphertext = packed.slice(pkLen + nonceLen);
    const my_sk = decodeBase64(my_seckey_b64);

    const plaintext = nacl.box.open(ciphertext, nonce, ephemeral_pk, my_sk);
    if (!plaintext) return null;

    return JSON.parse(encodeUTF8(plaintext)) as EnvelopePayload;
  } catch {
    return null;
  }
}
